// renderPixelLoaderFrame.ts
export type PixelStarFrameOpts = {
  // Visual size of the square output (CSS pixels)
  size?: number;

  // —— Copy of your loader props so it matches exactly ——
  grid?: number;
  stepMs?: number;
  travelMs?: number;
  groupPauseMs?: number;
  pauseMs?: number;
  baseGrey?: string;
  selectedGrey?: string;
  goldShades?: string[];
  background?: string; // use same background; pass 'transparent' if you want alpha
  showNumbers?: boolean;
  ringPx?: number;
  ringScale?: number;

  /**
   * Progress from 0..1 measured by *tiles filled*, not time.
   * 0.5 → exactly half the star's tiles are marked filled.
   */
  progress?: number;
};

/**
 * Renders a single frame of your PixelLoader, identical styling,
 * at the given progress (by filled tile count), and returns a PNG data URL.
 */
export function renderPixelStarLogo({
  size = 250,

  grid = 21,
  baseGrey = "#d3d8e3",
  selectedGrey = "#b0b7c3",
  goldShades = [
    "#FFCC33",
    "#FFC233",
    "#FFD65C",
    "#FFE08A",
    "#FFB300",
    "#FFE699",
  ],
  background = "#ffffff",
  showNumbers = true,
  ringPx = 3,
  ringScale = 2,

  progress = 0.5,
}: PixelStarFrameOpts = {}): string {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas not available");

  const dpr = 1; // to match your component (you set dpr=1 there)
  canvas.width = Math.floor(size * dpr);
  canvas.height = Math.floor(size * dpr);
  canvas.style.width = `${size}px`;
  canvas.style.height = `${size}px`;

  // ---- palette geometry (device pixels) ----
  const PAL_COUNT = 6;
  const palGap = Math.round(10 * dpr);
  const palSize = Math.round(48 * dpr);
  const palRadius = Math.round(6 * dpr);
  const palY = size - Math.round(18 * dpr) - palSize;
  let palX0 = 0;

  function roundRectPath(
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ) {
    if (!ctx) return;
    const rr = Math.min(r, Math.min(w, h) / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
    ctx.lineTo(x + rr, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
    ctx.lineTo(x, y + rr);
    ctx.quadraticCurveTo(x, y, x + rr, y);
    ctx.closePath();
  }

  // --- sizing: full square like your loader ---
  const side = size;
  const cell = Math.floor(side / grid);
  const offset = Math.floor((side - cell * grid) / 2);

  // --- star polygon (exactly your math) ---
  const starPoly: Array<{ x: number; y: number }> = [];
  const outerR = 0.46,
    innerR = 0.2,
    cx = 0.5,
    cy = 0.5;
  for (let k = 0; k < 10; k++) {
    const ang = -Math.PI / 2 + (k * Math.PI) / 5;
    const r = k % 2 === 0 ? outerR : innerR;
    starPoly.push({ x: cx + r * Math.cos(ang), y: cy + r * Math.sin(ang) });
  }
  const pip = (x: number, y: number) => {
    let inside = false;
    for (let i = 0, j = starPoly.length - 1; i < starPoly.length; j = i++) {
      const xi = starPoly[i].x,
        yi = starPoly[i].y;
      const xj = starPoly[j].x,
        yj = starPoly[j].y;
      const inter =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-9) + xi;
      if (inter) inside = !inside;
    }
    return inside;
  };

  // --- Build cells in serpentine order, assign groups 1..6 (exactly like yours) ---
  type Cell = {
    x: number;
    y: number;
    cx: number;
    cy: number;
    group: number;
    filled: boolean;
  };
  const cells: Cell[] = [];
  for (let gy = 0; gy < grid; gy++) {
    const row: Cell[] = [];
    for (let gx = 0; gx < grid; gx++) {
      const px = (gx + 0.5) / grid;
      const py = (gy + 0.5) / grid;
      if (pip(px, py)) {
        const cxp = offset + gx * cell + cell / 2;
        const cyp = offset + gy * cell + cell / 2;
        row.push({ x: gx, y: gy, cx: cxp, cy: cyp, group: 0, filled: false });
      }
    }
    if (gy % 2 === 1) row.reverse();
    cells.push(...row);
  }
  for (let i = 0; i < cells.length; i++) {
    cells[i].group = (i % 6) + 1;
  }
  const groups: number[][] = Array.from({ length: 6 }, () => []);
  cells.forEach((c, i) => groups[c.group - 1].push(i));

  // ---- Determine fill state at a given progress (by tile count) ----
  const totalTiles = cells.length;
  const targetFilled = Math.max(
    0,
    Math.min(totalTiles, Math.round(totalTiles * progress))
  );

  // We follow your fill order: by group 1..6 in index order
  const fillOrder: number[] = [];
  for (let g = 0; g < 6; g++) fillOrder.push(...groups[g]);

  // Mark filled tiles
  for (let i = 0; i < targetFilled; i++) {
    const ci = fillOrder[i];
    if (ci !== undefined) cells[ci].filled = true;
  }

  // Compute nowGroup and the next target (to position the ring like your loop)
  // If we've filled N tiles, the next target is fillOrder[N] (unless we're at the end).
  const nextIndex = Math.min(targetFilled, fillOrder.length - 1);
  const nextCellIdx = fillOrder[nextIndex] ?? fillOrder[fillOrder.length - 1];
  const nowGroup = cells[nextCellIdx]?.group ?? 6;

  // Ring position: we place it exactly on the *next* target center,
  // which is what your loop does at the start of "hold" or "betweenGroups".
  const toX = cells[nextCellIdx]?.cx ?? size / 2;
  const toY = cells[nextCellIdx]?.cy ?? size / 2;

  // Palette renderer (copied to match your look)
  const drawPalette = (currentGroup: number) => {
    const totalW = PAL_COUNT * palSize + (PAL_COUNT - 1) * palGap;
    palX0 = Math.round((canvas.width - totalW) / 2);

    for (let i = 0; i < PAL_COUNT; i++) {
      const x = palX0 + i * (palSize + palGap);
      const y = palY;
      const color = goldShades[i % goldShades.length] || goldShades[0];

      const groupIndex = i; // 0..5
      const done = groups[groupIndex].every((ci) => cells[ci].filled);
      const isCurrent = i + 1 === currentGroup;

      // base swatch
      ctx.save();
      roundRectPath(x, y, palSize, palSize, palRadius);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.lineWidth = Math.max(1, Math.floor(2 * dpr));
      ctx.strokeStyle = "rgba(0,0,0,0.12)";
      ctx.stroke();
      ctx.restore();

      if (isCurrent) {
        const glow = Math.max(3, Math.floor(3 * dpr));
        ctx.save();
        ctx.lineWidth = Math.max(1, Math.floor(3 * dpr));
        ctx.strokeStyle = "rgba(255,255,255,0.95)";
        roundRectPath(
          x - glow,
          y - glow,
          palSize + glow * 2,
          palSize + glow * 2,
          palRadius + glow
        );
        ctx.stroke();
        ctx.shadowColor = "rgba(0,0,0,0.25)";
        ctx.shadowBlur = Math.floor(6 * dpr);
        ctx.shadowOffsetY = Math.floor(2 * dpr);
        roundRectPath(x, y, palSize, palSize, palRadius);
        ctx.strokeStyle = "rgba(0,0,0,0.08)";
        ctx.stroke();
        ctx.restore();
      }

      // number or tick
      ctx.save();
      const cxp = x + palSize / 2;
      const cyp = y + palSize / 2;
      if (done) {
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = Math.max(2, Math.floor(3 * dpr));
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        const s = palSize * 0.42;
        ctx.beginPath();
        ctx.moveTo(cxp - s * 0.6, cyp + s * 0.05);
        ctx.lineTo(cxp - s * 0.1, cyp + s * 0.5);
        ctx.lineTo(cxp + s * 0.65, cyp - s * 0.45);
        ctx.stroke();
      } else {
        ctx.fillStyle = "#ffffff";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = `${Math.floor(
          palSize * 0.6
        )}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto`;
        ctx.globalAlpha = isCurrent ? 1 : 0.9;
        ctx.fillText(String(i + 1), cxp, cyp + Math.floor(0.02 * palSize));
        ctx.globalAlpha = 1;
      }
      ctx.restore();
    }
  };

  // ---- Draw frame (identical logic/appearance to your drawFrame) ----
  // bg
  ctx.fillStyle = background === "transparent" ? "rgba(0,0,0,0)" : background;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // text setup
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `${Math.floor(
    cell * 0.55
  )}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto`;

  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];

    // 1) filled tiles = gold
    if (c.filled) {
      ctx.fillStyle =
        goldShades[(c.group - 1) % goldShades.length] || goldShades[0];
    }
    // 2) unfilled tiles of the current group = selected grey
    else if (c.group === nowGroup) {
      ctx.fillStyle = selectedGrey;
    }
    // 3) other unfilled tiles = base grey
    else {
      ctx.fillStyle = baseGrey;
    }

    ctx.fillRect(offset + c.x * cell, offset + c.y * cell, cell, cell);

    // numbers on unfilled tiles
    if (showNumbers && !c.filled) {
      ctx.fillStyle =
        c.group === nowGroup ? "rgba(0,0,0,0.75)" : "rgba(0,0,0,0.55)";
      ctx.fillText(String(c.group), c.cx, c.cy);
    }
  }

  // ring — color matches current group
  const ringColor =
    goldShades[(nowGroup - 1) % goldShades.length] || goldShades[0];
  ctx.save();
  ctx.lineWidth = ringPx * dpr;
  ctx.strokeStyle = ringColor;
  const r = (cell * ringScale) / 2;
  ctx.beginPath();
  ctx.arc(toX, toY, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  // palette strip
  drawPalette(nowGroup);

  return canvas.toDataURL("image/png");
}

export function downloadLogo() {
  const dataUrl = renderPixelStarLogo({
    size: 1024, // iOS/Android source size
    grid: 21, // match your loader
    baseGrey: "#d3d8e3",
    goldShades: [
      "#FFCC33",
      "#FFC233",
      "#FFD65C",
      "#FFE08A",
      "#FFB300",
      "#FFE699",
    ],
    background: "transparent",
  });

  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = "pixel-star-logo.png";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
